import { PLANS, planFuer, DELIVERY_OVERAGE_CENTS_PER_MILLION, type PlanKey, type PlanLimits } from './plans.js';

/**
 * Berechnung einer Monatsrechnung.
 *
 * Bewusst eine reine Funktion ohne Datenbank und ohne Stripe: die Preislogik
 * ist der Teil, der stimmen muss, und sie laesst sich so ohne Konto und ohne
 * Netz durchrechnen. Stripe bekommt spaeter nur noch fertige Betraege.
 *
 * Warum nicht Stripe die Abrechnung fuehren lassen: unsere Zusagen widersprechen
 * dem Modell fertiger Abo-Preise. Der Mengennachlass gilt erst ab der fuenften
 * Domain, jede Domain kann in einer anderen Stufe liegen, und eine Stufe darf
 * sich nie von selbst erhoehen. Das alles in Abo-Positionen abzubilden hiesse,
 * die Regeln auf zwei Systeme zu verteilen. Also rechnen wir, und Stripe zieht
 * ein.
 */

/** Ab dieser Zahl von Domains gilt der Mengennachlass. */
export const MENGENNACHLASS_AB_DOMAIN = 5;

/** Nachlass auf jede Domain ab der Grenze, in Prozent. */
export const MENGENNACHLASS_PROZENT = 30;

export interface AbrechnungsDomain {
  siteId: string;
  domain: string;
  /** Tarif dieser Domain. Faellt auf "free" zurueck, wenn unbekannt. */
  plan: string;
  /** Ausgelieferte Banner im Abrechnungszeitraum. */
  auslieferungen: number;
  /**
   * Anteil des Monats, in dem die Domain aktiv war, zwischen 0 und 1.
   *
   * Fuer die anteilige Abrechnung bei Wechsel oder Kuendigung nach § 6 Abs. 7
   * der AGB. Fehlt der Wert, gilt der volle Monat.
   */
  anteil?: number;
}

export interface Rechnungsposten {
  siteId: string | null;
  bezeichnung: string;
  /** Betrag in Cent. Negativ bei Nachlaessen. */
  betragCents: number;
}

export interface Abrechnung {
  posten: Rechnungsposten[];
  /** Summe aller Posten in Cent. Ohne Umsatzsteuer, siehe Hinweis. */
  summeCents: number;
  /**
   * Hinweis, der auf die Rechnung gehoert.
   *
   * Solange die Kleinunternehmerregelung gilt, darf keine Umsatzsteuer
   * ausgewiesen werden, und der Grund muss auf der Rechnung stehen
   * (§ 14 Abs. 4 Nr. 8 UStG).
   */
  steuerhinweis: string;
}

export const KLEINUNTERNEHMER_HINWEIS =
  'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.';

/**
 * Rechnet einen Monat ab.
 *
 * Reihenfolge: erst die Grundpreise je Domain, absteigend sortiert, damit der
 * Nachlass die guenstigsten Domains trifft und nicht die teuersten. Das ist
 * die fuer den Kunden unguenstigere Lesart und damit die, die man zusagen
 * kann, ohne sich zu verrechnen. Danach die Aufschlaege fuer Auslieferungen.
 */
export function rechneAb(
  domains: AbrechnungsDomain[],
  optionen: { kleinunternehmer?: boolean } = {},
): Abrechnung {
  const posten: Rechnungsposten[] = [];

  const bezahlt = domains
    .map((d) => ({ ...d, tarif: planFuer(d.plan) }))
    .filter((d) => d.tarif.priceCents > 0)
    .sort((a, b) => b.tarif.priceCents - a.tarif.priceCents);

  bezahlt.forEach((eintrag, index) => {
    const anteil = begrenze(eintrag.anteil ?? 1);
    const grund = Math.round(eintrag.tarif.priceCents * anteil);
    posten.push({
      siteId: eintrag.siteId,
      bezeichnung: `${eintrag.domain}, Tarif ${eintrag.tarif.label}${anteil < 1 ? `, anteilig ${Math.round(anteil * 100)} %` : ''}`,
      betragCents: grund,
    });

    if (index + 1 >= MENGENNACHLASS_AB_DOMAIN) {
      posten.push({
        siteId: eintrag.siteId,
        bezeichnung: `Mengennachlass ${MENGENNACHLASS_PROZENT} % auf ${eintrag.domain}`,
        betragCents: -Math.round((grund * MENGENNACHLASS_PROZENT) / 100),
      });
    }
  });

  // Aufschlaege getrennt von den Grundpreisen, damit sie auf der Rechnung als
  // eigene Zeile stehen und der Kunde sie nachrechnen kann.
  //
  // Im Tarif Frei wird nicht nachberechnet. § 6 Abs. 8 der AGB sagt zu, dass
  // er unentgeltlich ist; eine Rechnung waere ein Widerspruch dazu, und ein
  // Konto ohne Zahlungsmittel koennte sie ohnehin nicht begleichen.
  // Ueberschreitungen dort fuehren zu einer Ansprache, nicht zu einem Posten.
  for (const eintrag of domains) {
    const tarif = planFuer(eintrag.plan);
    if (tarif.priceCents === 0) continue;
    const mehr = mehrkosten(eintrag.auslieferungen, tarif);
    if (mehr <= 0) continue;
    const millionen = Math.ceil(
      (eintrag.auslieferungen - tarif.deliveryAllowance) / 1_000_000,
    );
    posten.push({
      siteId: eintrag.siteId,
      bezeichnung: `${eintrag.domain}, ${millionen} angefangene Mio Auslieferungen über dem Kontingent`,
      betragCents: mehr,
    });
  }

  const summeCents = posten.reduce((summe, p) => summe + p.betragCents, 0);

  return {
    posten,
    summeCents,
    steuerhinweis: optionen.kleinunternehmer === false ? '' : KLEINUNTERNEHMER_HINWEIS,
  };
}

function mehrkosten(auslieferungen: number, tarif: PlanLimits): number {
  const ueber = auslieferungen - tarif.deliveryAllowance;
  if (ueber <= 0) return 0;
  return Math.ceil(ueber / 1_000_000) * DELIVERY_OVERAGE_CENTS_PER_MILLION;
}

function begrenze(wert: number): number {
  if (!Number.isFinite(wert)) return 1;
  return Math.min(1, Math.max(0, wert));
}

/**
 * Domains im Tarif Frei, die ihr Kontingent ueberschreiten.
 *
 * Sie werden nicht berechnet, aber jemand sollte mit ihnen sprechen.
 */
export function freieUeberschreitungen(domains: AbrechnungsDomain[]): AbrechnungsDomain[] {
  return domains.filter((eintrag) => {
    const tarif = planFuer(eintrag.plan);
    return tarif.priceCents === 0 && eintrag.auslieferungen > tarif.deliveryAllowance;
  });
}

/** Betrag in Cent als deutscher Text, etwa "24,00 €". */
export function alsEuro(cents: number): string {
  return `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Alle Tarife als Liste, fuer Auswahlfelder im Dashboard. */
export function tarifListe(): PlanLimits[] {
  return (Object.keys(PLANS) as PlanKey[]).map((key) => PLANS[key]);
}
